import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  FaProjectDiagram,
  FaCheckCircle,
  FaSpinner,
  FaArrowRight,
  FaPlus,
  FaLightbulb,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaStar,
  FaExclamationTriangle
} from 'react-icons/fa';
import { isBudgetExceededError } from '../../shared/heatmapViewUtils';
import './JourneyMiningTab.css';

const GENERIC_EVENTS = new Set(['unmapped_click', 'clicked_next_step', 'clicked_home']);

export function humanizeStep(name) {
  if (!name) return name;
  let s = String(name)
    .replace(/^entered_field_placeholder_/, 'Entered ')
    .replace(/^entered_field_/, 'Entered ')
    .replace(/^dropdown_select_/, 'Selected ')
    .replace(/^radio_input_/, 'Selected ')
    .replace(/^checkbox_input_/, 'Checked ')
    .replace(/^clicked_/, 'Clicked ')
    .replace(/_selected$/, '')
    .replace(/_only$/, '')
    .replace(/_/g, ' ')
    .trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function isUuidString(str) {
  if (!str || typeof str !== 'string') return false;
  const s = str.trim();
  return (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s) ||
    /^[0-9a-f]{32}$/i.test(s) ||
    /^[0-9a-f]{64}$/i.test(s) ||
    /^[0-9a-f]{8}$/i.test(s)
  );
}

export function autoLabelFlow(eventNames = [], fallbackTitle = 'Detected Flow') {
  const meaningful = eventNames.filter((e) => e && !GENERIC_EVENTS.has(e) && !isUuidString(e));
  if (meaningful.length === 0) {
    if (fallbackTitle && !isUuidString(fallbackTitle)) {
      return fallbackTitle.replace(/^cluster-/, '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) + ' Flow';
    }
    return 'Primary Navigation Journey';
  }
  const first = meaningful[0];
  const cleaned = first
    .replace(/^clicked_/, '')
    .replace(/^entered_field_placeholder_/, '')
    .replace(/^entered_field_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
  return cleaned ? `${cleaned} Flow` : 'Primary Navigation Journey';
}

/**
 * Tab 4: ML.NET Sequential Pattern Mining & Automated Flow Generation.
 * Matches the rich Golden Flow Review layout with auto-naming, humanized step pills,
 * screen URLs, search filtering, variant collapsibles, and Golden Flow migration to Content Studio.
 */
export default function JourneyMiningTab({
  mining,
  actions = [],
  pages = [],
  selectedPageId = '',
  miningOptions = {},
  onMiningOptionsChange = null,
  onGenerateFlow,
  generatingFlow = false,
  flowGenerationResult = null,
  isLoading = false,
  error = null
}) {
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedClusterKey, setSelectedClusterKey] = useState('');
  const [isGoldenModal, setIsGoldenModal] = useState(false);
  const [flowName, setFlowName] = useState('');
  const [flowDescription, setFlowDescription] = useState('');
  const [generateError, setGenerateError] = useState(null);
  const [successBanner, setSuccessBanner] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedClusters, setExpandedClusters] = useState({});

  // Active golden flow mappings: { [clusterKey]: candidateId }
  const [activeGoldenFlows, setActiveGoldenFlows] = useState(() => {
    try {
      const stored = localStorage.getItem('cognior_active_golden_flows');
      return stored ? JSON.parse(stored) : {};
    } catch (_) {
      return {};
    }
  });

  // Merge actions from props and from backend mining response
  const actionsMap = useMemo(() => {
    const map = {};
    (actions || []).forEach((act) => {
      if (act && act.actionId) map[act.actionId] = act;
    });
    (mining?.actions || []).forEach((act) => {
      if (act && act.actionId) map[act.actionId] = act;
    });
    return map;
  }, [actions, mining?.actions]);

  const pagesMap = useMemo(() => {
    return pages.reduce((acc, p) => {
      acc[p.pageId] = p;
      return acc;
    }, {});
  }, [pages]);

  const clusters = useMemo(() => mining?.clusters || [], [mining?.clusters]);
  const totalJourneys = mining?.journeys || 0;

  const handleToggleExpand = (clusterKey) => {
    setExpandedClusters((prev) => ({
      ...prev,
      [clusterKey]: !prev[clusterKey]
    }));
  };

  const getActionDisplayName = useCallback((actId, stepIdx = 0, fallbackPrefix = 'Step') => {
    const act = actionsMap[actId];
    if (act?.name && !isUuidString(act.name)) return humanizeStep(act.name);
    if (act?.captureKey && !isUuidString(act.captureKey)) return humanizeStep(act.captureKey);
    if (typeof actId === 'string' && !isUuidString(actId)) return humanizeStep(actId);
    return `${fallbackPrefix} ${stepIdx + 1}`;
  }, [actionsMap]);

  const handleOpenGenerateModal = (candidate, clusterKey, isGolden = false) => {
    setSelectedCandidate(candidate);
    setSelectedClusterKey(clusterKey);
    setIsGoldenModal(isGolden);

    const firstActionId = candidate.actionIds?.[0];
    const firstActionName = firstActionId ? getActionDisplayName(firstActionId, 0, '') : '';
    const baseClusterName = clusterKey.replace(/^cluster-/, '');

    if (isGolden) {
      setFlowName(firstActionName ? `Golden Flow: ${firstActionName}` : `Golden Flow: ${baseClusterName}`);
      setFlowDescription('');
    } else {
      setFlowName(`Auto Flow: ${baseClusterName}`);
      setFlowDescription('');
    }
    setGenerateError(null);
  };

  const handleConfirmGenerate = async (e) => {
    e.preventDefault();
    if (!selectedCandidate || !flowName.trim()) return;

    setGenerateError(null);

    // 1. If it's Golden Flow, immediately mark it active in UI and localStorage
    if (isGoldenModal && selectedClusterKey) {
      setActiveGoldenFlows((prev) => {
        const next = { ...prev, [selectedClusterKey]: selectedCandidate.candidateId };
        try {
          localStorage.setItem('cognior_active_golden_flows', JSON.stringify(next));
        } catch (_) { }
        return next;
      });
    }

    try {
      const payload = {
        name: flowName.trim(),
        candidateId: selectedCandidate.candidateId,
        actionIds: selectedCandidate.actionIds,
        configurationRevision: mining?.configurationRevision
      };
      if (flowDescription.trim()) {
        payload.flowDescription = flowDescription.trim();
      }

      // Try DAP generation in backend (if it errors, it won't block Golden Flow status)
      const result = await onGenerateFlow(payload);

      setSelectedCandidate(null);
      setSuccessBanner({
        ...result,
        isGolden: isGoldenModal,
        name: result?.name || flowName.trim(),
        stepsCount: result?.stepsCount ?? (selectedCandidate.actionIds?.length || 0)
      });
    } catch (err) {
      if (isGoldenModal) {
        // Golden Flow is already saved locally; close modal and show banner
        setSelectedCandidate(null);
        setSuccessBanner({
          isGolden: true,
          name: flowName.trim(),
          stepsCount: selectedCandidate.actionIds?.length || 0,
          status: 'Golden Flow Active'
        });
      } else {
        setGenerateError(err.message || 'Failed to generate flow');
      }
    }
  };

  // Filter clusters according to search query
  const filteredClusters = useMemo(() => {
    if (!searchQuery.trim()) return clusters;
    const q = searchQuery.trim().toLowerCase();

    return clusters.filter((cluster) => {
      if (cluster.clusterKey.toLowerCase().includes(q)) return true;
      if (
        cluster.topActions &&
        cluster.topActions.some((a) => {
          const name = getActionDisplayName(a, 0);
          return name.toLowerCase().includes(q) || a.toLowerCase().includes(q);
        })
      ) {
        return true;
      }

      // Check candidate action names, screens, or routes
      const hasActionMatch = (cluster.candidates || []).some((c) =>
        (c.actionIds || []).some((actId, idx) => {
          const act = actionsMap[actId];
          const name = getActionDisplayName(actId, idx);
          if (name.toLowerCase().includes(q)) return true;
          if (act?.screen && act.screen.toLowerCase().includes(q)) return true;
          return false;
        })
      );
      return hasActionMatch;
    });
  }, [clusters, searchQuery, actionsMap, getActionDisplayName]);

  const sortedClusters = useMemo(() => {
    return [...filteredClusters].sort((a, b) => {
      const maxStepsA = Math.max(...(a.candidates || []).map((c) => (c.actionIds || []).length), 0);
      const maxStepsB = Math.max(...(b.candidates || []).map((c) => (c.actionIds || []).length), 0);

      // Prioritize rich multi-step flows (e.g. 3+ steps) over short 1-2 step flows
      if (maxStepsA >= 3 && maxStepsB < 3) return -1;
      if (maxStepsB >= 3 && maxStepsA < 3) return 1;

      // Secondary: step count descending
      if (maxStepsA !== maxStepsB) return maxStepsB - maxStepsA;

      // Tertiary: session traffic volume
      return (b.journeys || 0) - (a.journeys || 0);
    });
  }, [filteredClusters]);

  const renderVariant = (cand, cluster, idx, isPrimary = false) => {
    const isExplicitGolden = activeGoldenFlows[cluster.clusterKey] === cand.candidateId;
    const isGenerated = flowGenerationResult?.candidateId === cand.candidateId || isExplicitGolden;
    const supportPercentDisplay =
      cand.supportPercent != null
        ? `${Number(cand.supportPercent).toFixed(1)}%`
        : '100%';

    // const stepCount = (cand.actionIds || []).length;

    return (
      <div
        key={cand.candidateId}
        className={`candidate-box variant-card ${isPrimary ? 'primary-variant' : ''} ${isGenerated ? 'active-golden-card' : ''
          }`}
        data-testid={`candidate-${cand.candidateId}`}
      >
        <div className="candidate-meta variant-header">
          <div className="variant-title-group">
            <span className="variant-title">
              Variant {idx + 1} &bull; Validated in {cand.support} session
              {cand.support === 1 ? '' : 's'}
            </span>
            <span className="support-badge">
              <strong>{supportPercentDisplay}</strong> Support
            </span>
            {isGenerated && (
              <span className="active-badge">
                <FaStar /> Currently Active Golden Flow
              </span>
            )}
          </div>
          <div className="variant-actions">
            <button
              type="button"
              className="btn-create-flow"
              onClick={() => handleOpenGenerateModal(cand, cluster.clusterKey, false)}
              disabled={generatingFlow}
              title="Create Flow from this Pattern"
            >
              <FaPlus /> Create Flow from this Pattern
            </button>
            <button
              type="button"
              className={`btn-set-golden ${isGenerated ? 'active' : ''}`}
              onClick={() => {
                setActiveGoldenFlows((prev) => {
                  const next = { ...prev, [cluster.clusterKey]: cand.candidateId };
                  try {
                    localStorage.setItem('cognior_active_golden_flows', JSON.stringify(next));
                  } catch (_) { }
                  return next;
                });
              }}
              disabled={generatingFlow}
            >
              <FaCheckCircle /> {isGenerated ? 'Re-confirm Golden Flow' : 'Set as Golden Flow'}
            </button>
          </div>
        </div>

        {/* Step sequence arrows */}
        <div className="candidate-steps-flow steps-container">
          {cand.actionIds.map((actId, stepIdx) => {
            const actMeta = actionsMap[actId] || {};
            const stepName = getActionDisplayName(actId, stepIdx);
            const page = pagesMap[actMeta.pageId];
            const screenRoute =
              actMeta.screen || actMeta.routePattern || actMeta.targetUrl || page?.name || '/services';
            const isLast = stepIdx === cand.actionIds.length - 1;

            return (
              <React.Fragment key={actId}>
                <div className="step-node step-pill">
                  <span className="node-num">{stepIdx + 1}</span>
                  <div className="step-text-wrap">
                    <span className="node-name">{stepName}</span>
                    <small className="step-screen">{screenRoute}</small>
                  </div>
                </div>
                {!isLast && <FaArrowRight className="step-arrow" />}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  const renderClusterCard = (cluster) => {
    const candidates = cluster.candidates || [];
    const firstCand = candidates[0];
    const restCandidates = candidates.slice(1);
    const isExpanded = Boolean(expandedClusters[cluster.clusterKey]);

    const flowCandidateEventNames = firstCand
      ? firstCand.actionIds.map((id, i) => getActionDisplayName(id, i))
      : (cluster.topActions || []).map((id, i) => getActionDisplayName(id, i));
    const flowDisplayName = autoLabelFlow(flowCandidateEventNames, cluster.clusterKey);

    return (
      <div
        key={cluster.clusterKey}
        className="cluster-card flow-card"
        data-testid={`cluster-${cluster.clusterKey}`}
      >
        <div className="cluster-header flow-card-header">
          <div className="flow-title-wrap">
            <h4 className="cluster-title flow-name">
              <span className="flow-title-text">{flowDisplayName}</span>
              <span className="cluster-key-tag" title={cluster.clusterKey}>
                {cluster.clusterKey}
              </span>
            </h4>
            <span className="cluster-sub flow-meta">
              {cluster.journeys} Session{cluster.journeys === 1 ? '' : 's'} (
              {totalJourneys > 0 ? ((cluster.journeys / totalJourneys) * 100).toFixed(1) : 0}% of traffic) &bull;{' '}
              {candidates.length} Variant{candidates.length === 1 ? '' : 's'}
            </span>
          </div>

          {/* Top Actions Chips - User Friendly Labels */}
          {cluster.topActions && cluster.topActions.length > 0 && (
            <div className="top-actions-chips">
              {cluster.topActions.map((actKey, i) => {
                const isUuid = isUuidString(actKey);
                const resolved = actionsMap[actKey];
                const displayName = resolved?.name && !isUuidString(resolved.name)
                  ? humanizeStep(resolved.name)
                  : (isUuid ? `Action ${i + 1}` : actKey);
                return (
                  <span key={actKey} className="top-action-chip" title={actKey}>
                    {displayName}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Common Events Summary */}
        {cluster.topActions && cluster.topActions.length > 0 && (
          <div className="flow-top-events">
            <strong>Common events:</strong>{' '}
            {cluster.topActions
              .map((actKey, i) => {
                const act = actionsMap[actKey];
                if (act?.name && !isUuidString(act.name)) return humanizeStep(act.name);
                if (!isUuidString(actKey)) return humanizeStep(actKey);
                return `Action ${i + 1}`;
              })
              .filter(Boolean)
              .join(' • ')}
          </div>
        )}

        {/* Candidate Sequences */}
        <div className="candidates-list">
          {firstCand ? (
            renderVariant(firstCand, cluster, 0, true)
          ) : (
            <div className="empty-variant-msg">No candidate sequences discovered for this cluster.</div>
          )}

          {restCandidates.length > 0 && (
            <div className="more-variants-section">
              <button
                type="button"
                className="link-toggle-variants"
                onClick={() => handleToggleExpand(cluster.clusterKey)}
              >
                {isExpanded ? (
                  <>
                    <FaChevronUp /> Hide variants
                  </>
                ) : (
                  <>
                    <FaChevronDown /> View {restCandidates.length} other variant
                    {restCandidates.length === 1 ? '' : 's'}
                  </>
                )}
              </button>
              {isExpanded && (
                <div className="additional-variants-list">
                  {restCandidates.map((cand, i) => renderVariant(cand, cluster, i + 1, false))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="mining-tab-loading">
        <div className="spinner" />
        <p>Executing sequential pattern mining across behavioral sessions...</p>
      </div>
    );
  }

  return (
    <div className="journey-mining-tab" data-testid="journey-mining-tab">
      <div className="mining-header-card">
        <div className="mining-header-info">
          <h3>Behavioral Journey Mining &amp; Golden Flow Review</h3>
          <p>
            Review detected user journeys grouped by business process, evaluate validated sequence support, and establish the definitive Golden Flow to push directly into Content Studio.
          </p>
        </div>
        <div className="mining-summary-kpis">
          <div className="mining-kpi">
            <span className="kpi-label">Analyzed Journeys</span>
            <span className="kpi-val">{totalJourneys.toLocaleString()}</span>
          </div>
          <div className="mining-kpi">
            <span className="kpi-label">Discovered Clusters</span>
            <span className="kpi-val">{clusters.length}</span>
          </div>
        </div>
      </div>

      {/* Sticky Search & Filter Toolbar */}
      <div className="mining-toolbar">
        <div className="search-wrapper">
          <FaSearch className="search-icon" />
          <input
            type="text"
            className="search-box"
            placeholder="Search flows (e.g. 'all-services', 'checkout', 'submit')..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search flows"
          />
        </div>

        <div className="mining-toolbar-actions">
          <span className="result-count">
            {searchQuery
              ? `${filteredClusters.length} of ${clusters.length} flows`
              : `${clusters.length} total flows`}
          </span>
        </div>
      </div>

      {/* 422 Budget Alert / Generic Error Banner */}
      {error && isBudgetExceededError(error) ? (
        <div className="mining-budget-alert" role="alert" data-testid="mining-budget-alert">
          <div className="budget-alert-header">
            <FaExclamationTriangle className="alert-icon" />
            <strong>Heatmap Query Budget Exceeded (422)</strong>
          </div>
          <p className="budget-alert-desc">
            Backend query ceiling was exceeded (scanned interaction rows &gt; 2,000 or PrefixSpan recursions &gt; 10,000).
          </p>
          <div className="budget-mitigation-steps">
            <strong>Recommended Mitigations:</strong>
            <ul>
              <li><strong>Shorten Date Window:</strong> Select a narrower date range (e.g. 2–3 days) using the From/To filters above.</li>
              <li><strong>Filter by Page:</strong> Select a specific Page from the dropdown above instead of site-wide analysis.</li>
              <li><strong>Filter by Device:</strong> Narrow the device class filter (desktop/mobile) to analyze smaller interaction volumes.</li>
            </ul>
          </div>
        </div>
      ) : error ? (
        <div className="mining-error-banner" role="alert">
          {error}
        </div>
      ) : null}

      {successBanner && (
        <div className="mining-success-banner" role="alert" data-testid="mining-flow-success-banner">
          <FaCheckCircle className="success-icon" />
          <div className="success-content">
            <strong>
              {successBanner.isGolden
                ? 'Golden Flow Established & Migrated to Content Studio!'
                : 'Flow Created in Content Studio (Pending Review)!'}
            </strong>
            <p>
              Flow {successBanner.name ? <span>"<strong>{successBanner.name}</strong>" </span> : null}
              (ID: <code>{successBanner.flowId}</code>)
              {successBanner.stepsCount ? ` with ${successBanner.stepsCount} steps` : ''} was pushed to Content Studio with status <em>{successBanner.status}</em>.
            </p>
          </div>
          <button type="button" className="close-success-btn" onClick={() => setSuccessBanner(null)}>
            ✕
          </button>
        </div>
      )}

      {/* Clusters List */}
      {clusters.length === 0 ? (
        <div className="mining-empty-state" data-testid="mining-empty-state">
          <FaLightbulb className="empty-icon" />
          <h4>No Candidate Patterns Discovered</h4>
          <p>
            Ensure capture is active and sufficient user interactions exist in the selected measurement window.
          </p>
        </div>
      ) : filteredClusters.length === 0 ? (
        <div className="mining-empty-state">
          <FaSearch className="empty-icon" />
          <h4>No Matching Flows Found</h4>
          <p>No detected flows matched your search query "{searchQuery}".</p>
        </div>
      ) : (
        <div className="clusters-grid">
          {sortedClusters.map(renderClusterCard)}
        </div>
      )}

      {/* Modal: Generate Flow / Set as Golden Flow */}
      {selectedCandidate && (
        <div className="mining-modal-overlay">
          <div className="mining-modal-card">
            <h4>
              {isGoldenModal ? (
                <>
                  <FaStar style={{ color: '#eab308', marginRight: 8 }} />
                  Set as Golden Flow &amp; Migrate to Content Studio
                </>
              ) : (
                'Generate UserFlow from Candidate Pattern'
              )}
            </h4>
            <p className="modal-sub">
              {isGoldenModal
                ? 'Establish this sequence as the definitive Golden Flow for this journey and push it directly into Content Studio for authoring.'
                : 'This pattern was mined from real user sessions. Generating this flow will create it in Pending status for Content Studio authors.'}
            </p>

            {generateError && (
              <div className="modal-error-banner">{generateError}</div>
            )}

            <form onSubmit={handleConfirmGenerate}>
              <div className="form-group">
                <label htmlFor="mining-flow-name">Flow Name</label>
                <input
                  id="mining-flow-name"
                  type="text"
                  required
                  maxLength={200}
                  value={flowName}
                  onChange={(e) => setFlowName(e.target.value)}
                  className="modal-input"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="mining-flow-desc">Description (Optional)</label>
                <input
                  id="mining-flow-desc"
                  type="text"
                  maxLength={2000}
                  value={flowDescription}
                  onChange={(e) => setFlowDescription(e.target.value)}
                  className="modal-input"
                  placeholder="e.g. Primary happy-path sequence for all services..."
                />
              </div>

              <div className="sequence-preview">
                <label>Sequence Steps ({selectedCandidate.actionIds.length})</label>
                <ol className="preview-list">
                  {selectedCandidate.actionIds.map((actId, i) => {
                    const actMeta = actionsMap[actId] || {};
                    const page = pagesMap[actMeta.pageId];
                    const screenRoute =
                      actMeta.screen || actMeta.routePattern || actMeta.targetUrl || page?.name || '/services';
                    return (
                      <li key={actId}>
                        <strong>Step {i + 1}:</strong> {getActionDisplayName(actId, i)}{' '}
                        <span className="preview-screen-badge">({screenRoute})</span>
                      </li>
                    );
                  })}
                </ol>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setSelectedCandidate(null)}
                  disabled={generatingFlow}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`btn-submit ${isGoldenModal ? 'btn-golden-submit' : ''}`}
                  disabled={generatingFlow || !flowName.trim()}
                >
                  {generatingFlow ? (
                    <FaSpinner className="spinning" />
                  ) : isGoldenModal ? (
                    <FaCheckCircle />
                  ) : (
                    <FaProjectDiagram />
                  )}
                  <span>
                    {generatingFlow
                      ? 'Migrating to Content Studio...'
                      : isGoldenModal
                        ? 'Set as Golden Flow & Migrate'
                        : 'Generate Flow'}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

JourneyMiningTab.propTypes = {
  mining: PropTypes.shape({
    configurationRevision: PropTypes.number,
    journeys: PropTypes.number,
    clusters: PropTypes.array,
    actions: PropTypes.array
  }),
  actions: PropTypes.array,
  pages: PropTypes.array,
  selectedPageId: PropTypes.string,
  miningOptions: PropTypes.object,
  onMiningOptionsChange: PropTypes.func,
  onGenerateFlow: PropTypes.func.isRequired,
  generatingFlow: PropTypes.bool,
  flowGenerationResult: PropTypes.object,
  isLoading: PropTypes.bool,
  error: PropTypes.string
};
